/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.SYSTEM.AI_CONFIG — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/AiConfigPage.tsx. A DISTINCT
 * presentation (KPI tiles + dense card list + facet chips + bottom-sheet edit)
 * over the SAME data/logic layer only:
 *   - reads via apiDb: the CSM.AI.ENABLED global flag (csm_system_parameters) and
 *     the per-screen opt-in rows (csm_ai_screen_config), resolving each
 *     page_internal_code to its ACTIVE-locale display name via csm_page_master +
 *     _tl (§8, §17.B4 — never a raw id/code reaches render),
 *   - every write (per-screen toggles / max context rows, and adding a screen)
 *     routes through the Common Routine via routedUpdate / routedInsert — §2 single
 *     write path; never a direct apiDb write.
 * It never re-renders the desktop grid; §16. The desktop still owns the redacted-PII
 * field list (shown read-only here, like MobileUserEditSheet's roles) and the
 * interaction-log viewer.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Plus, Sparkles, Bot } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
} from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'CSM.SYSTEM.AI_CONFIG';

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

interface ScreenConfigRow {
  ai_screen_config_id: number;
  page_internal_code: string;
  is_enabled: 'Y' | 'N';
  allow_nlq: 'Y' | 'N';
  allow_smart_paste: 'Y' | 'N';
  redacted_fields: string[];
  max_context_rows: number;
}

interface PageOption {
  code: string;
  name: string;
}

type StatusFacet = 'all' | 'enabled' | 'disabled';

interface TlRow { locale_code: string; display_text: string }

const DEFAULT_MAX_CONTEXT_ROWS = 50;
const MAX_CONTEXT_ROWS_CAP = 500;

// ---------------------------------------------------------------------------
// Main screen
// ---------------------------------------------------------------------------

export function MobileAiConfigPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [configs, setConfigs] = useState<ScreenConfigRow[]>([]);
  const [pageNames, setPageNames] = useState<Record<string, string>>({});
  const [pageList, setPageList] = useState<PageOption[]>([]);
  const [globalOn, setGlobalOn] = useState<boolean | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('all');
  const [selected, setSelected] = useState<ScreenConfigRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      // Global switch (CSM.AI.ENABLED) — the same key the desktop console reads.
      const { data: gp } = await apiDb
        .from('csm_system_parameters')
        .select('parameter_value')
        .eq('internal_code', 'CSM.AI.ENABLED')
        .maybeSingle();
      setGlobalOn((gp as { parameter_value?: string } | null)?.parameter_value === 'Y');

      const { data: cfg, error: cErr } = await apiDb
        .from('csm_ai_screen_config')
        .select('ai_screen_config_id, page_internal_code, is_enabled, allow_nlq, allow_smart_paste, redacted_fields, max_context_rows')
        .order('page_internal_code', { ascending: true });
      if (cErr) throw cErr;
      setConfigs((cfg ?? []) as ScreenConfigRow[]);

      // Resolve page codes to translated names for display (§17.B4 — never the raw
      // code) and build the pick-list for the "add screen" flow.
      const { data: rawPages, error: pErr } = await apiDb
        .from('csm_page_master')
        .select('internal_code, csm_page_master_tl ( locale_code, display_text )')
        .eq('is_active', 'Y')
        .order('internal_code');
      if (pErr) throw pErr;

      type RawPage = { internal_code: string; csm_page_master_tl?: TlRow[] };
      const names: Record<string, string> = {};
      const list: PageOption[] = [];
      for (const p of (rawPages ?? []) as RawPage[]) {
        const tls = p.csm_page_master_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        const name = exact?.display_text ?? fb?.display_text ?? p.internal_code;
        names[p.internal_code] = name;
        list.push({ code: p.internal_code, name });
      }
      setPageNames(names);
      setPageList(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (page labels follow it, §8).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const pageLabel = (code: string) => pageNames[code] ?? code;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return configs.filter((c) => {
      if (statusFacet === 'enabled' && c.is_enabled !== 'Y') return false;
      if (statusFacet === 'disabled' && c.is_enabled !== 'N') return false;
      if (!q) return true;
      return pageLabel(c.page_internal_code).toLowerCase().includes(q)
        || c.page_internal_code.toLowerCase().includes(q);
    });
    // pageNames feeds pageLabel; include it so relabeling re-filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs, filter, statusFacet, pageNames]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.ai.all', 'All') },
    { key: 'enabled', label: t('mobile.ai.enabled', 'Enabled') },
    { key: 'disabled', label: t('mobile.ai.disabled', 'Disabled') },
  ];

  const enabledCount = useMemo(() => configs.filter((c) => c.is_enabled === 'Y').length, [configs]);
  const nlqCount = useMemo(() => configs.filter((c) => c.allow_nlq === 'Y').length, [configs]);
  const pasteCount = useMemo(() => configs.filter((c) => c.allow_smart_paste === 'Y').length, [configs]);

  // Pages not yet configured — the pick-list for the add sheet.
  const availablePages = useMemo(() => {
    const taken = new Set(configs.map((c) => c.page_internal_code));
    return pageList.filter((p) => !taken.has(p.code));
  }, [configs, pageList]);

  const featureSummary = (c: ScreenConfigRow): string => {
    const parts: string[] = [];
    if (c.allow_nlq === 'Y') parts.push(t('mobile.ai.nlq_short', 'Smart Query'));
    if (c.allow_smart_paste === 'Y') parts.push(t('mobile.ai.paste_short', 'Smart Paste'));
    return parts.length ? parts.join(' · ') : t('mobile.ai.no_features', 'No AI features');
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.ai.title', 'Contextual AI')}
        description={t('mobile.ai.description', 'Which screens offer AI assistance, and the features each one exposes.')}
      />

      <MobileStatStrip
        stats={[
          { value: configs.length.toLocaleString(), label: t('mobile.ai.kpi_screens', 'Screens') },
          { value: enabledCount.toLocaleString(), label: t('mobile.ai.kpi_enabled', 'Enabled') },
          { value: nlqCount.toLocaleString(), label: t('mobile.ai.nlq_short', 'Smart Query') },
          { value: pasteCount.toLocaleString(), label: t('mobile.ai.paste_short', 'Smart Paste') },
        ]}
      />

      {/* Global status — mirrors the desktop banner (CSM.AI.ENABLED). */}
      {globalOn !== null && (
        <MobileContentCard>
          <MobileDetailRow
            label={t('mobile.ai.global', 'Contextual AI')}
            value={
              <MobileStatusPill tone={globalOn ? 'ok' : 'wait'}>
                {globalOn
                  ? t('mobile.ai.global_on', 'Enabled platform-wide')
                  : t('mobile.ai.global_off', 'Disabled platform-wide')}
              </MobileStatusPill>
            }
          />
        </MobileContentCard>
      )}

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.ai.search', 'Search screens')}
          aria-label={t('mobile.ai.search', 'Search screens')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <div className="m-chips">
        {canAdd && (<button
          type="button"
          className="m-chip"
          disabled={availablePages.length === 0}
          onClick={() => setAdding(true)}
        >
          <Plus size={14} /> {t('mobile.ai.add', 'Add screen')}
        </button>)}
      </div>

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.ai.none', 'No screens match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((c) => (
            <MobileCardRow
              key={c.ai_screen_config_id}
              lead={<Sparkles />}
              leadTone={c.is_enabled === 'Y' ? 'blue' : 'slate'}
              title={pageLabel(c.page_internal_code)}
              subtitle={`${c.page_internal_code} · ${featureSummary(c)}`}
              trailing={
                <MobileStatusPill tone={c.is_enabled === 'Y' ? 'ok' : 'no'}>
                  {c.is_enabled === 'Y' ? t('mobile.ai.on', 'On') : t('mobile.ai.off', 'Off')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(c)}
            />
          ))}
        </MobileContentCard>
      )}

      <MobileAiConfigEditSheet
        session={session}
        row={selected}
        pageName={selected ? pageLabel(selected.page_internal_code) : ''}
        onClose={() => setSelected(null)}
        onSaved={() => { setSelected(null); void reload(); }}
      />

      <MobileAiConfigAddSheet
        session={session}
        open={adding}
        pages={availablePages}
        onClose={() => setAdding(false)}
        onSaved={() => { setAdding(false); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the per-screen toggles (Enabled / Smart Query / Smart Paste) and
// max context rows, saved through the Common Routine (routedUpdate) — §2. The
// redacted-PII field list lives on the desktop and is shown read-only here
// (mirrors MobileUserEditSheet's read-only roles).
// ---------------------------------------------------------------------------

interface MobileAiConfigEditSheetProps {
  session: AuthSession;
  row: ScreenConfigRow | null;
  pageName: string;
  onClose: () => void;
  onSaved: () => void;
}

function MobileAiConfigEditSheet({ session, row, pageName, onClose, onSaved }: MobileAiConfigEditSheetProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  // §17.B13 relation: recent AI activity for this screen. Fetched when the sheet
  // opens rather than with the list — it is detail, and the log is large.
  const [logs, setLogs] = useState<{ ai_interaction_log_id: number; status: string; task_type: string; log_timestamp: string }[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [nlq, setNlq] = useState(true);

  useEffect(() => {
    if (!row) { setLogs([]); return; }
    let cancelled = false;
    void (async () => {
      const { data } = await apiDb
        .from('csm_ai_interaction_log')
        .select('ai_interaction_log_id, status, task_type, log_timestamp')
        .eq('page_code', row.page_internal_code)
        .order('log_timestamp', { ascending: false })
        .limit(20);
      if (!cancelled) setLogs((data ?? []) as { ai_interaction_log_id: number; status: string; task_type: string; log_timestamp: string }[]);
    })();
    return () => { cancelled = true; };
  }, [row]);
  const [smartPaste, setSmartPaste] = useState(true);
  const [maxRows, setMaxRows] = useState(String(DEFAULT_MAX_CONTEXT_ROWS));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different row is opened.
  useEffect(() => {
    if (!row) return;
    setEnabled(row.is_enabled === 'Y');
    setNlq(row.allow_nlq === 'Y');
    setSmartPaste(row.allow_smart_paste === 'Y');
    setMaxRows(String(row.max_context_rows));
    setError(null);
  }, [row]);

  const save = async () => {
    if (!row) return;
    const parsed = parseInt(maxRows, 10);
    const clamped = Number.isNaN(parsed) ? 0 : Math.max(0, Math.min(MAX_CONTEXT_ROWS_CAP, parsed));
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(
      session, 'csm_ai_screen_config', 'ai_screen_config_id', row.ai_screen_config_id, {
        is_enabled: enabled ? 'Y' : 'N',
        allow_nlq: nlq ? 'Y' : 'N',
        allow_smart_paste: smartPaste ? 'Y' : 'N',
        max_context_rows: clamped,
      },
    );
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;
  const redactedText = row && row.redacted_fields.length
    ? row.redacted_fields.join(', ')
    : t('common.none', 'None');

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.ai.edit_sheet', 'Edit AI screen')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Sparkles size={20} /></span>
              <span>
                <div className="m-nm">{pageName}</div>
                <div className="m-em">{row.page_internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.ai.enabled', 'Enabled')}</div>
                  <div className="m-formrow-s">{t('mobile.ai.enabled_hint', 'AI assistance available on this screen')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  className={'m-switch ' + (enabled ? 'm-on' : 'm-off')}
                  onClick={() => setEnabled((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.ai.nlq', 'Smart Query')}</div>
                  <div className="m-formrow-s">{t('mobile.ai.nlq_hint', 'Natural-language querying')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={nlq}
                  className={'m-switch ' + (nlq ? 'm-on' : 'm-off')}
                  onClick={() => setNlq((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.ai.paste', 'Smart Paste')}</div>
                  <div className="m-formrow-s">{t('mobile.ai.paste_hint', 'AI-assisted paste into forms')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={smartPaste}
                  className={'m-switch ' + (smartPaste ? 'm-on' : 'm-off')}
                  onClick={() => setSmartPaste((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-ai-maxrows">{t('mobile.ai.max_rows', 'Max context rows')}</label>
                <input
                  id="m-ai-maxrows"
                  className="m-inp"
                  type="number"
                  min={0}
                  max={MAX_CONTEXT_ROWS_CAP}
                  value={maxRows}
                  onChange={(e) => setMaxRows(e.target.value)}
                />
              </div>
              <div className="m-formrow">
                <label>{t('mobile.ai.redacted', 'Redacted fields')}</label>
                <div className="m-formrow-ro">
                  {redactedText}
                  <span className="m-formrow-hint"> · {t('mobile.ai.redacted_desktop', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            {/* §17.B13 — the row's related records, stacked one group per block. */}
            <MobileContentCard>
              <MobileRelatedChildren
                session={session}
                groups={[
                  {
                    key: 'activity',
                    table: 'csm_ai_interaction_log', pkColumn: 'ai_interaction_log_id',
                    title: t('mobile.ai.related.activity', 'Recent Activity'),
                    items: logs.map(l => ({
                      id: l.ai_interaction_log_id,
                      label: l.task_type,
                      sub: `${formatDateTime(l.log_timestamp)} · ${l.status}`,
                    })),
                  },
                ]}
              />
            </MobileContentCard>
            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Add sheet — opt a new screen into contextual AI. Pick an unconfigured page,
// insert through the Common Routine (routedInsert) — §2. Defaults mirror the
// desktop "add screen" flow (disabled, Smart Query + Smart Paste on).
// ---------------------------------------------------------------------------

interface MobileAiConfigAddSheetProps {
  session: AuthSession;
  open: boolean;
  pages: PageOption[];
  onClose: () => void;
  onSaved: () => void;
}

function MobileAiConfigAddSheet({ session, open, pages, onClose, onSaved }: MobileAiConfigAddSheetProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the picker with the first available page each time the sheet opens.
  useEffect(() => {
    if (!open) return;
    setCode(pages[0]?.code ?? '');
    setError(null);
  }, [open, pages]);

  const save = async () => {
    if (!code) { setError(t('mobile.ai.pick_required', 'Select a screen to enable.')); return; }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedInsert(session, 'csm_ai_screen_config', {
      page_internal_code: code,
      is_enabled: 'N',
      allow_nlq: 'Y',
      allow_smart_paste: 'Y',
      redacted_fields: [],
      max_context_rows: DEFAULT_MAX_CONTEXT_ROWS,
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.ai.add_sheet', 'Add AI screen')}>
        <div className="m-grabber" />
        {open && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Bot size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.ai.add', 'Add screen')}</div>
                <div className="m-em">{t('mobile.ai.add_sub', 'Opt a screen into contextual AI')}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-ai-page">{t('mobile.ai.screen', 'Screen')}</label>
                {pages.length === 0 ? (
                  <div className="m-formrow-ro">{t('mobile.ai.all_configured', 'Every screen is already configured.')}</div>
                ) : (
                  <select id="m-ai-page" className="m-sel" value={code} onChange={(e) => setCode(e.target.value)}>
                    {pages.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
                  </select>
                )}
              </div>
              <div className="m-formrow">
                <label>{t('mobile.ai.defaults', 'Defaults')}</label>
                <div className="m-formrow-ro">
                  {t('mobile.ai.add_defaults', 'Disabled · Smart Query on · Smart Paste on')}
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy || pages.length === 0 || !code} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('mobile.ai.add', 'Add screen')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
